"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import clsx from "clsx";
import { useTranslations, useLocale } from "next-intl";
import { fetchHighlights } from "@/lib/api/datosGenerales/fetchHighlights";
import { Highlights } from "@/lib/api/datosGenerales/types/highlights";
import ExternalLinkModal from "@/components/ExternalLinkModal/ExternalLinkModal";
import SectionShell from "@/ui/elements/sectionShell";
import MediaCard from "@/ui/elements/mediaCard";
import { type Locale } from "@/i18n/config";

const AUTOPLAY_MS = 5000;
/** Tiempo que el autoplay espera tras una interacción del usuario */
const RESUME_MS = 6000;
/** Dos posiciones que difieren menos que esto son la misma parada */
const STOP_EPSILON = 4;

/**
 * Las cards son más anchas que las de «Qué hacer» (2,5 por pantalla frente a 3)
 * para que un destacado se lea como tal, manteniendo el mismo 16:10.
 */
const SLIDE_WIDTH =
  "w-[85%] md:w-[calc((100%-1.5rem)/2)] lg:w-[calc((100%-3rem)/2.5)]";

const prefersReducedMotion = () =>
  typeof window !== "undefined" &&
  window.matchMedia("(prefers-reduced-motion: reduce)").matches;

export default function InfoCarouselSection() {
  const t = useTranslations("highlight");
  const locale = useLocale() as Locale;

  const [data, setData] = useState<Highlights[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedUrl, setSelectedUrl] = useState<string | null>(null);

  /** Posiciones de scroll alcanzables; cada una es un punto de abajo */
  const [stops, setStops] = useState<number[]>([]);
  const [active, setActive] = useState(0);
  const [isPaused, setIsPaused] = useState(false);

  const trackRef = useRef<HTMLDivElement>(null);
  const slideRefs = useRef<(HTMLDivElement | null)[]>([]);
  const activeRef = useRef(0);
  const resumeTimeout = useRef<ReturnType<typeof setTimeout> | null>(null);
  const rafId = useRef(0);

  useEffect(() => {
    activeRef.current = active;
  }, [active]);

  useEffect(() => {
    async function loadData() {
      try {
        setLoading(true);
        const highlights = await fetchHighlights(locale);
        setData(highlights);
      } catch (error) {
        console.error("Error al cargar los Highlights:", error);
      } finally {
        setLoading(false);
      }
    }
    loadData();
  }, [locale]);

  /**
   * Las paradas se miden del DOM, no se asumen.
   *
   * Con varias cards por pantalla, las últimas ya no pueden colocarse contra el
   * borde izquierdo: sus posiciones se saturan en el final del recorrido. Si a
   * cada card se le da un punto, esos últimos puntos apuntan todos al mismo
   * sitio y dejan de corresponderse con lo que se ve. Aquí las posiciones
   * repetidas se colapsan, así que hay exactamente un punto por parada real.
   */
  const measureStops = useCallback(() => {
    const track = trackRef.current;
    if (!track) return;

    const max = Math.max(0, track.scrollWidth - track.clientWidth);
    const trackLeft = track.getBoundingClientRect().left;

    const next: number[] = [];
    slideRefs.current.forEach((slide) => {
      if (!slide) return;
      const raw =
        track.scrollLeft + (slide.getBoundingClientRect().left - trackLeft);
      const pos = Math.min(Math.max(raw, 0), max);
      const last = next[next.length - 1];
      if (last === undefined || Math.abs(pos - last) > STOP_EPSILON) {
        next.push(pos);
      }
    });

    setStops(next);
  }, []);

  useEffect(() => {
    const track = trackRef.current;
    if (!track || loading || !data.length) return;

    measureStops();
    const observer = new ResizeObserver(measureStops);
    observer.observe(track);
    return () => observer.disconnect();
  }, [data, loading, measureStops]);

  /** El punto activo es la parada más cercana al scroll real */
  const syncActive = useCallback(() => {
    const track = trackRef.current;
    if (!track || stops.length === 0) return;

    const x = track.scrollLeft;
    let best = 0;
    let bestDistance = Infinity;
    stops.forEach((stop, i) => {
      const distance = Math.abs(stop - x);
      if (distance < bestDistance) {
        bestDistance = distance;
        best = i;
      }
    });
    setActive(best);
  }, [stops]);

  useEffect(() => {
    syncActive();
  }, [syncActive]);

  const handleScroll = useCallback(() => {
    cancelAnimationFrame(rafId.current);
    rafId.current = requestAnimationFrame(syncActive);
  }, [syncActive]);

  useEffect(() => () => cancelAnimationFrame(rafId.current), []);

  const goTo = useCallback(
    (i: number) => {
      const track = trackRef.current;
      if (!track || stops.length === 0) return;
      const clamped = Math.max(0, Math.min(i, stops.length - 1));
      track.scrollTo({
        left: stops[clamped],
        behavior: prefersReducedMotion() ? "auto" : "smooth",
      });
    },
    [stops],
  );

  /** Pausa el autoplay mientras el usuario navega y lo reanuda al quedarse quieto */
  const deferAutoplay = useCallback(() => {
    setIsPaused(true);
    if (resumeTimeout.current) clearTimeout(resumeTimeout.current);
    resumeTimeout.current = setTimeout(() => setIsPaused(false), RESUME_MS);
  }, []);

  useEffect(() => {
    return () => {
      if (resumeTimeout.current) clearTimeout(resumeTimeout.current);
    };
  }, []);

  useEffect(() => {
    if (isPaused || stops.length < 2 || prefersReducedMotion()) return;

    const timer = setInterval(() => {
      goTo((activeRef.current + 1) % stops.length);
    }, AUTOPLAY_MS);

    return () => clearInterval(timer);
  }, [isPaused, stops, goTo]);

  const handleContinue = () => {
    if (selectedUrl) {
      window.open(selectedUrl, "_blank");
      setSelectedUrl(null);
    }
  };

  const lastIndex = data.length - 1;

  return (
    <>
      <SectionShell
        eyebrow={t("eyebrow")}
        title={t("title")}
        subtitle={t("subtitle")}
        // Gris y no blanco: las cards son sólo imagen, así que su color lo pone
        // el arte. Sobre blanco puro la sombra al pasar el cursor chocaba con
        // el fondo de la zona de los puntos.
        tone="muted"
      >
        <div
          ref={trackRef}
          onScroll={handleScroll}
          // Se escuchan gestos, no `scroll`: un scroll suave dispara decenas de
          // eventos y el propio autoplay acabaría pausándose solo.
          onPointerDown={deferAutoplay}
          onTouchStart={deferAutoplay}
          onWheel={deferAutoplay}
          onKeyDown={deferAutoplay}
          // `overflow-x-auto` obliga al eje vertical a recortar también, y el
          // recorte cae en el borde de la caja de padding. Sin este colchón, al
          // pasar el cursor la card sube 4 px y se corta por arriba, y su sombra
          // se corta por abajo contra la zona de los puntos. Los márgenes
          // negativos devuelven el espacio para que la maqueta no se mueva.
          className="highlight-track flex gap-6 overflow-x-auto snap-x snap-mandatory pt-4 -mt-4 pb-10 -mb-10"
        >
          {loading
            ? Array.from({ length: 3 }).map((_, i) => (
                <div
                  key={i}
                  className={clsx(
                    "shrink-0 snap-start rounded-2xl overflow-hidden ring-1 ring-black/5 shadow-lg",
                    SLIDE_WIDTH,
                  )}
                >
                  <div className="aspect-[16/10] bg-gradient-to-r from-gray-200 via-gray-300 to-gray-200 animate-pulse relative overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white/40 to-transparent animate-shimmer"></div>
                  </div>
                </div>
              ))
            : data.map((item, i) => (
                <div
                  key={item.id}
                  ref={(el) => {
                    slideRefs.current[i] = el;
                  }}
                  className={clsx(
                    "shrink-0 flex",
                    // La última alinea por su borde derecho: así el final del
                    // recorrido es también una parada válida del snap.
                    i === lastIndex ? "snap-end" : "snap-start",
                    SLIDE_WIDTH,
                  )}
                >
                  <MediaCard
                    image={item.banner}
                    // El arte del banner ya trae su propia tipografía: la card es
                    // sólo la imagen y `nombre` queda como etiqueta accesible.
                    title={item.nombre?.trim() || undefined}
                    actionLabel={t("viewMore")}
                    onAction={() => item.url && setSelectedUrl(item.url)}
                    sizes="(max-width: 768px) 85vw, (max-width: 1024px) 48vw, 500px"
                    priority={i === 0}
                    showBody={false}
                  />
                </div>
              ))}
        </div>

        {/* Un punto por parada real: pulsar el punto i lleva exactamente a la
            posición que hace que el punto i quede activo. */}
        {!loading && stops.length > 1 && (
          <div className="flex justify-center gap-2 mt-8">
            {stops.map((_, i) => (
              <button
                key={i}
                type="button"
                onClick={() => {
                  deferAutoplay();
                  goTo(i);
                }}
                className={clsx(
                  "h-2 rounded-full transition-all duration-300 cursor-pointer focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--botonesSecundarios-iconoLlamativo)] focus-visible:ring-offset-2",
                  i === active
                    ? "w-8 bg-[var(--botonesSecundarios-iconoLlamativo)]"
                    : "w-2 bg-[var(--bordes-iconos-texto-2)] hover:bg-[var(--subtitulos-descripciones-iconos)]",
                )}
                aria-label={t("goTo", { n: i + 1 })}
                aria-current={i === active}
              />
            ))}
          </div>
        )}

        <style jsx>{`
          .highlight-track {
            scrollbar-width: none;
            -ms-overflow-style: none;
          }
          .highlight-track::-webkit-scrollbar {
            display: none;
          }
        `}</style>
      </SectionShell>

      <ExternalLinkModal
        isOpen={!!selectedUrl}
        onClose={() => setSelectedUrl(null)}
        onContinue={handleContinue}
      />
    </>
  );
}
